import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'data-fetching-client';
import * as RequestState from 'ecosystem-marketplace-core/constants/RequestState';
import * as PlanType from 'ecosystem-marketplace-core/constants/pricings/PlanType';
import * as AssetUninstallableReason from '../constants/AssetUninstallableReason';
import { GET_ASSET_INSTALLABILITY } from '../queries/assetInstallabilityQuery';
import { getIsAdminPreviewApp, getIsPublicApp } from '../selectors/app';
import { getHasMarketplaceAssetAccess, getHasRequiredSubscription, getIsUngatedForPurchaseScopedFeatures, makeGetMissingPortalScopes // @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module '../s... Remove this comment to see the full error message
} from '../selectors/auth';
export function useAssetInstallability({
  offeringId,
  planType,
  requiredScopes
}) {
  const hasMarketplaceAssetAccess = useSelector(getHasMarketplaceAssetAccess);
  const hasRequiredSubscription = useSelector(getHasRequiredSubscription);
  const isAdminPreviewApp = useSelector(getIsAdminPreviewApp);
  const isPublicApp = useSelector(getIsPublicApp);
  const isUngatedForPurchaseScopedFeatures = useSelector(getIsUngatedForPurchaseScopedFeatures);
  const {
    loading,
    error,
    data
  } = useQuery(GET_ASSET_INSTALLABILITY, {
    skip: isAdminPreviewApp || isPublicApp,
    variables: {
      offeringId
    }
  });
  const requestState = useMemo(() => {
    if (isAdminPreviewApp || isPublicApp) {
      return RequestState.SUCCEEDED;
    }

    if (loading) {
      return RequestState.PENDING;
    }

    if (data) {
      return RequestState.SUCCEEDED;
    }

    if (error) {
      return RequestState.FAILED;
    }

    return RequestState.UNINITIALIZED;
  }, [data, error, isAdminPreviewApp, isPublicApp, loading]);
  const getMissingPortalScopes = useMemo(makeGetMissingPortalScopes, []);
  const missingPortalScopes = useSelector(state => {
    if (!requiredScopes) {
      return [];
    }

    return getMissingPortalScopes(state, requiredScopes);
  });
  const totalMissingPortalScopes = missingPortalScopes.length;
  const assetUninstallableReason = useMemo(() => {
    if (isAdminPreviewApp) {
      return AssetUninstallableReason.FORBIDDEN_ADMIN_PREVIEW;
    } else if (isPublicApp) {
      return AssetUninstallableReason.FORBIDDEN_PUBLIC;
    }

    if (!data) {
      return null;
    }

    const {
      assetInstallability: {
        exceedsAssetLimit,
        installPending,
        installed,
        pendingPaymentExpiry,
        purchased,
        selfMade,
        uninstallPending
      }
    } = data;

    if (installed) {
      return AssetUninstallableReason.ALREADY_INSTALLED;
    } else if (exceedsAssetLimit) {
      return AssetUninstallableReason.EXCEEDS_ASSET_LIMIT;
    } else if (purchased && !installed) {
      return AssetUninstallableReason.PURCHASED_NOT_INSTALLED;
    } else if (pendingPaymentExpiry) {
      return AssetUninstallableReason.PENDING_PAYMENT;
    } else if (installPending) {
      return AssetUninstallableReason.PENDING_INSTALL;
    } else if (uninstallPending) {
      return AssetUninstallableReason.PENDING_UNINSTALL;
    } else if (selfMade) {
      return AssetUninstallableReason.PROVIDER_OFFERING;
    } else if (!hasRequiredSubscription) {
      return AssetUninstallableReason.MISSING_REQUIRED_SUBSCRIPTION;
    } else if (!hasMarketplaceAssetAccess) {
      return AssetUninstallableReason.MISSING_MARKETPLACE_ASSET_ACCESS;
    } else if (totalMissingPortalScopes && planType !== PlanType.FREE && !isUngatedForPurchaseScopedFeatures) {
      return AssetUninstallableReason.MISSING_HUB_SCOPES;
    }

    return null;
  }, [data, hasMarketplaceAssetAccess, hasRequiredSubscription, isAdminPreviewApp, isPublicApp, isUngatedForPurchaseScopedFeatures, planType, totalMissingPortalScopes]);
  const result = useMemo(() => {
    return {
      assetUninstallableReason,
      installability: data ? data.assetInstallability : null,
      missingPortalScopes,
      requestState
    };
  }, [assetUninstallableReason, data, missingPortalScopes, requestState]);
  return result;
}